import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, Clock, Gauge, Layers3, Radio } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getEventAggregate } from '../../../api/events'
import { listTriggers } from '../../../api/triggers'
import { LevelBadge, StatusBadge } from '../../../components/common/Status'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { RescueFlowSummary } from '../../events/RescueFlowPanel'
import { Button } from '../../../components/ui/button'
import { ScrollArea } from '../../../components/ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../components/ui/sheet'
import { Skeleton } from '../../../components/ui/skeleton'
import { formatAbsTs, formatCoord, formatMag } from '../../../lib/format'
import { eventTypeLabel, eventStatusLabel } from '../../../lib/alert-level'
import { useUiStore } from '../../../stores/ui'

export function EventDrawer() {
  const navigate = useNavigate()
  const selectedEventId = useUiStore((s) => s.selectedEventId)
  const open = Boolean(selectedEventId)

  const query = useQuery({
    queryKey: ['events', 'detail', selectedEventId],
    queryFn: () => getEventAggregate(selectedEventId!),
    enabled: open,
    retry: 1,
  })

  const triggerQuery = useQuery({
    queryKey: ['triggers', { eventId: selectedEventId, pageSize: 30 }],
    queryFn: () => listTriggers({ eventId: selectedEventId, pageSize: 30 }),
    enabled: open,
    retry: 1,
  })

  const handleClose = (next: boolean) => {
    if (!next) useUiStore.getState().selectEvent(undefined)
  }

  return (
    <Sheet open={open} onOpenChange={handleClose} modal={false}>
      <SheetContent
        className="w-[480px] gap-0 sm:max-w-[92vw]"
        overlay={false}
        // 非模态抽屉：点击地图不关闭，由选中事件变化驱动（点震中即切换详情）
        onInteractOutside={(e) => e.preventDefault()}
      >
        {selectedEventId && (
          <QueryBoundary
            query={query}
            skeleton={
              <div className="space-y-3 p-5">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-20 rounded-lg" />
                <Skeleton className="h-40 rounded-lg" />
              </div>
            }
            className="flex min-h-0 flex-1 flex-col"
          >
            {({ event }) => (
              <>
                <SheetHeader>
                  <div className="flex items-center gap-2">
                    <LevelBadge alertLevel={event.alertLevel} severity={event.severity} pulse={event.status === 'pending'} />
                    <StatusBadge status={event.status} labels={eventStatusLabel} />
                  </div>
                  <SheetTitle className="mt-2 text-lg">{event.name || event.eventId}</SheetTitle>
                  <SheetDescription>
                    {eventTypeLabel(event.type)} · {formatAbsTs(event.originTime)}
                  </SheetDescription>
                </SheetHeader>

                <div className="border-b border-border px-5 py-4">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <MetricCell icon={<Gauge size={13} />} label="震级" value={formatMag(event.magnitude)} tone="text-critical" />
                    <MetricCell icon={<Layers3 size={13} />} label="深度" value={`${event.depth.toFixed(0)} km`} />
                    <MetricCell icon={<Radio size={13} />} label="触发" value={`${event.triggerCount}`} />
                    <MetricCell icon={<Clock size={13} />} label="预估到达" value={event.estimatedArrivalSec > 0 ? `${event.estimatedArrivalSec}s` : '—'} tone="text-warning" />
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-black/20 px-3 py-2">
                    <span className="font-mono text-[11px] text-muted-foreground">{formatCoord(event.lat, event.lon)}</span>
                    <span className="font-mono text-[10px] text-muted-foreground/60">{event.geohash}</span>
                  </div>
                </div>

                <ScrollArea className="min-h-0 flex-1 px-5 py-4">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    触发记录（{triggerQuery.data?.items.length ?? 0}）
                  </div>
                  <div className="space-y-1.5">
                    {(triggerQuery.data?.items ?? []).slice(0, 15).map((t) => (
                      <div key={t.triggerId} className="flex items-center gap-2 rounded-lg bg-foreground/[.03] px-3 py-2">
                        <span className="size-1.5 shrink-0 rounded-full" style={{ background: t.confirmed ? 'var(--success)' : 'var(--watch)' }} />
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-foreground/90">{t.deviceId}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          CAV {t.cav.toFixed(2)} · STA/LTA {t.staLtaRatio.toFixed(1)}
                        </span>
                      </div>
                    ))}
                    {triggerQuery.data?.items.length === 0 && (
                      <div className="rounded-lg bg-foreground/[.02] px-3 py-4 text-center text-[11px] text-muted-foreground">
                        暂无触发记录
                      </div>
                    )}
                  </div>

                  {/* 救援流向摘要：任务状态 + 轨迹/证据计数（轨迹在主地图渲染） */}
                  <RescueFlowSummary eventId={selectedEventId} />

                  {(query.data?.incidents.length ?? 0) > 0 && (
                    <>
                      <div className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        关联直报（{query.data?.incidents.length}）
                      </div>
                      <div className="space-y-1.5">
                        {query.data?.incidents.slice(0, 5).map((inc) => (
                          <div key={inc.reportId} className="flex items-center gap-2 rounded-lg bg-foreground/[.03] px-3 py-2">
                            <span className="min-w-0 flex-1 truncate text-[11px] text-foreground/90">
                              {eventTypeLabel(inc.type)} · {inc.description || inc.reportId}
                            </span>
                            <StatusBadge status={inc.status} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </ScrollArea>

                <div className="border-t border-border p-4">
                  <Button className="w-full" onClick={() => void navigate(`/events/${event.eventId}`)}>
                    打开完整时间线 <ArrowUpRight size={15} />
                  </Button>
                </div>
              </>
            )}
          </QueryBoundary>
        )}
      </SheetContent>
    </Sheet>
  )
}

function MetricCell({ icon, label, value, tone = 'text-foreground' }: {
  icon: React.ReactNode
  label: string
  value: string
  tone?: string
}) {
  return (
    <div className="rounded-lg bg-black/20 px-1 py-2">
      <div className="mb-1 flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`font-mono text-sm font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}
